import { compareFloatEqual } from './numUtil';
import { Vec2 } from './vec2';

/**
 * Generate a circular pattern of points
 * @param numParticles number of points
 * @param sep distance between points
 */
export function generateCircularPattern(
  numParticles: number,
  sep: number
): Vec2[] {
  if (numParticles <= 0) {
    return [];
  }
  if (numParticles == 1) {
    return [Vec2.zero()];
  }
  if (numParticles == 2) {
    return [
      { x: -sep / 2, y: 0 },
      { x: sep / 2, y: 0 }
    ];
  }

  const coordinates: Vec2[] = [];
  const thetaIncrement = (Math.PI * 2) / numParticles;
  const radius = sep / Math.sin(thetaIncrement);
  let theta = 0;
  for (let i = 0; i < numParticles; i++) {
    coordinates.push({
      x: radius * Math.cos(theta),
      y: radius * Math.sin(theta)
    });
    theta += thetaIncrement;
  }
  return coordinates;
}

/**
 * Generate a grid pattern of points
 * @param rows number of rows in the grid
 * @param cols number of columns in the grid
 * @param sepX distance between grid columns
 * @param sepY distance between grid rows
 */
export function generateGridPattern(
  rows: number,
  cols: number,
  sepX: number,
  sepY: number
): Vec2[] {
  const coordinates: Vec2[] = [];
  const gridCenter: Vec2 = {
    x: (sepX * (cols - 1)) / 2,
    y: (sepY * (rows - 1)) / 2
  };
  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      const vector = {
        x: j * sepX - gridCenter.x,
        y: i * sepY - gridCenter.y
      };
      coordinates.push(vector);
    }
  }
  return coordinates;
}

/**
 * Generate a triangular pattern of points.
 * Each consecutive row is offset by half of the distance between the previous row
 * and contains one less point than the previous row.
 *
 * @param numParticlesAtBase number of particles in triangle base, or number of rows in the triangle
 * @param sepX distance between points in single row
 * @param sepY distance between triangle columns
 */
export function generateTriangularPattern(
  numParticlesAtBase: number,
  sepX: number,
  sepY: number
) {
  const coordinates: Vec2[] = [];
  const triangleCenter: Vec2 = {
    x: ((numParticlesAtBase - 1) * sepX) / 2,
    y: ((numParticlesAtBase - 1) * sepY) / 3
  };
  for (let i = 0; i < numParticlesAtBase; i++) {
    for (let j = i; j < numParticlesAtBase; j++) {
      const vector = {
        x: j * sepX - triangleCenter.x - (i * sepX) / 2,
        y: i * sepY - triangleCenter.y
      };
      coordinates.push(vector);
    }
  }
  return coordinates;
}

/** Find a point in a set that is furthest from origin */
export function maxDistanceFromOriginInSet(positions: Vec2[]) {
  let maxDistance = 0;
  let position = Vec2.ZERO;
  for (const pos of positions) {
    const distanceSq = Vec2.normSquare(pos);
    if (distanceSq > maxDistance) {
      maxDistance = distanceSq;
      position = pos;
    }
  }
  return {
    distance: Math.sqrt(maxDistance),
    position
  };
}

/** Returns the position of two colliding atoms if any within specified threshold */
export function collisionWithinSet(positions: Vec2[], threshold: number) {
  for (let i = 0; i < positions.length; i++) {
    const pos = positions[i];
    for (let j = i + 1; j < positions.length; j++) {
      const pos2 = positions[j];
      const distance = Math.pow(Vec2.distanceSquare(pos, pos2), 1 / 2);
      if (!compareFloatEqual(distance, threshold) && distance < threshold) {
        return [pos, pos2];
      }
    }
  }
}
